import { supabase } from "./supabaseClient";

// Public event Likes — a NEW, separate feature from the private localStorage Saves.
// Persisted in Supabase (public."Event_Likes"), one row per user per event, guarded
// by a unique(user_id, event_id) constraint + RLS. Public reads the aggregate count;
// authenticated users create/delete only their own row. Reuses the existing client.
const TABLE = "Event_Likes";

async function fetchLikeInfoSingle(eventId, userId) {
  const { count } = await supabase
    .from(TABLE)
    .select("event_id", { count: "exact", head: true })
    .eq("event_id", eventId);
  let liked = false;
  if (userId) {
    const { data } = await supabase
      .from(TABLE)
      .select("event_id")
      .eq("event_id", eventId)
      .eq("user_id", userId)
      .maybeSingle();
    liked = !!data;
  }
  return { count: count || 0, liked };
}

// Every card mounts its own LikeButton, which used to cost two requests per card
// (support #279162: ~75 requests on one Discover load). Lookups made in the same tick
// are now coalesced into one count query and one own-likes query per batch; if the
// batch query fails, each card falls back to the original single lookup.
const LIKE_BATCH_MS = 20;
const LIKE_BATCH_MAX = 80;
const likeBatches = new Map();

export function fetchLikeInfo(eventId, userId) {
  return new Promise((resolve, reject) => {
    const key = userId || "";
    let batch = likeBatches.get(key);
    if (!batch) {
      batch = { waiters: [] };
      likeBatches.set(key, batch);
      setTimeout(() => flushLikeBatch(key, userId), LIKE_BATCH_MS);
    }
    batch.waiters.push({ eventId, resolve, reject });
  });
}

async function flushLikeBatch(key, userId) {
  const batch = likeBatches.get(key);
  likeBatches.delete(key);
  if (!batch) return;
  const ids = [...new Set(batch.waiters.map((w) => w.eventId))];
  const counts = new Map();
  const liked = new Set();
  let failed = false;
  try {
    for (let i = 0; i < ids.length; i += LIKE_BATCH_MAX) {
      const chunk = ids.slice(i, i + LIKE_BATCH_MAX);
      for (let from = 0; ; from += 1000) {
        const { data, error } = await supabase
          .from(TABLE)
          .select("event_id")
          .in("event_id", chunk)
          .order("event_id")
          .order("user_id")
          .range(from, from + 999);
        if (error) throw error;
        data.forEach((r) => counts.set(r.event_id, (counts.get(r.event_id) || 0) + 1));
        if (data.length < 1000) break;
      }
      if (userId) {
        const { data, error } = await supabase
          .from(TABLE)
          .select("event_id")
          .in("event_id", chunk)
          .eq("user_id", userId);
        if (error) throw error;
        data.forEach((r) => liked.add(r.event_id));
      }
    }
  } catch (e) {
    failed = true;
  }
  batch.waiters.forEach((w) => {
    if (failed) fetchLikeInfoSingle(w.eventId, userId).then(w.resolve, w.reject);
    else w.resolve({ count: counts.get(w.eventId) || 0, liked: liked.has(w.eventId) });
  });
}

export async function addLike(eventId, userId) {
  const { error } = await supabase.from(TABLE).insert({ event_id: eventId, user_id: userId });
  // Ignore unique-violation (already liked) so state stays consistent.
  if (error && !/duplicate|unique|23505/i.test(`${error.message || ""} ${error.code || ""}`)) {
    throw error;
  }
}

export async function removeLike(eventId, userId) {
  const { error } = await supabase
    .from(TABLE)
    .delete()
    .eq("event_id", eventId)
    .eq("user_id", userId);
  if (error) throw error;
}
